// Alternative version with fallback to French government API
export const fetchPostalCodesApi = async (
  setCitiesName,
  setPostalCodes,
  setPostal,
  setLoadingSearch,
  prefix,
  city,
  exact_match = false,
  currentPostalValue = ""
) => {
  try {
    city = city?.trim() || "";
    city = city?.toLowerCase() || "";

    if (city.length <= 2) {
      return; // Avoid searching for very short queries
    }

    // if spaces replace with -
    if (city.includes(" ")) {
      city = city.replace(/\s+/g, "-");
    }
    console.log("SSSearching for city:", city);
    setLoadingSearch(true);

    // First try your Google Places API
    if (!exact_match) {
      try {
        const queryParams = new URLSearchParams({
          text: city,
          country_code: "fr",
          language: "fr",
          limit: "10",
          include_postal_codes: "true",
        });

        const response = await fetch(
          `${prefix}places/autocomplete?${queryParams}`
        );

        if (response.ok) {
          const data = await response.json();
          console.log(
            "Google Places API response:",
            data.results,
            "exact_match:",
            exact_match
          );
          if (data.status === "OK" && data.results && data.results.length > 0) {
            // Success with Google Places API
            let cities_name = data.results.map(
              (place) => place.main_text || place.description
            );

            if (exact_match) {
              cities_name = cities_name.filter(
                (name) =>
                  name.toLowerCase() === city.toLowerCase() ||
                  name.toLowerCase().replace(/\s+/g, "-") ===
                    city.toLowerCase().replace(/\s+/g, "-")
              );
            }
            console.log("Fetched cities from Google Places API:", cities_name);

            // Add the exact user input as the first option
            const userInput = city.charAt(0).toUpperCase() + city.slice(1).replace(/-/g, ' ');
            if (!cities_name.some(c => c.toLowerCase() === userInput.toLowerCase())) {
              cities_name.unshift(userInput);
            }

            setCitiesName(cities_name);

            if (cities_name.length > 0) {
              let firstResult = data.results[0];
              let codesPostaux = firstResult.postal_code
                ? [firstResult.postal_code]
                : [];

              // Collect all postal codes from results
              const allPostalCodes = data.results
                .map(r => r.postal_code)
                .filter(Boolean);

              // Remove duplicates
              let uniquePostalCodes = [...new Set(allPostalCodes)];

              // Add current postal value if it exists and not already in the list
              if (currentPostalValue && !uniquePostalCodes.includes(currentPostalValue)) {
                uniquePostalCodes.unshift(currentPostalValue);
              }

              setPostalCodes(uniquePostalCodes.length > 0 ? uniquePostalCodes : codesPostaux);

              // Only set postal if user doesn't have a value yet
              if (!currentPostalValue) {
                setPostal(uniquePostalCodes[0] || codesPostaux[0] || "");
              }
            }

            setLoadingSearch(false);
            return; // Success, exit function
          }
        }
      } catch (googleError) {
        console.warn("Google Places API failed, trying fallback:", googleError);
      }
    }
    // Fallback to French government API
    console.log("Falling back to French government API");
    const response = await fetch(
      `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(city)}&fields=nom,code,codesPostaux&format=json&geometry=center&limit=10`
    );

    const data = await response.json();
    let cities_name = data.map((commune) => commune.nom);

    if (exact_match) {
      cities_name = cities_name.filter(
        (name) => name.toLowerCase() === city.toLowerCase()
      );
    }
    console.log("Fetched cities from fallback API:", cities_name);

    // Add the exact user input as the first option
    const userInput = city.charAt(0).toUpperCase() + city.slice(1).replace(/-/g, ' ');
    if (!cities_name.some(c => c.toLowerCase() === userInput.toLowerCase())) {
      cities_name.unshift(userInput);
    }

    setCitiesName(cities_name);

    let all_potential_matching_codes = data.flatMap(
      (commune) => commune.codesPostaux || []
    );

    // Add current postal value if it exists and not already in the list
    if (currentPostalValue && !all_potential_matching_codes.includes(currentPostalValue)) {
      all_potential_matching_codes.unshift(currentPostalValue);
    }

    console.log(
      "All potential matching postal codes:",
      all_potential_matching_codes
    );

    setPostalCodes(all_potential_matching_codes);

    // Only set postal if user doesn't have a value yet
    if (!currentPostalValue) {
      setPostal(all_potential_matching_codes[0] || "");
    }

    setLoadingSearch(false);
  } catch (error) {
    console.error("Error fetching postal codes:", error);
    setLoadingSearch(false);
    setCitiesName([]);
    setPostalCodes([]);
    setPostal("");
    return [];
  }
};

// Optimized version for better UX with debouncing
let searchTimeout;
export const fetchPostalCodesDebouncedApi = async (
  setCitiesName,
  setPostalCodes,
  setPostal,
  setLoadingSearch,

  city,
  exact_match = false,
  delay = 300
) => {
  // Clear previous timeout
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  // Don't search for very short queries
  if (city.trim().length < 2) {
    setCitiesName([]);
    setPostalCodes([]);
    setPostal("");
    return;
  }

  // Debounce the search
  searchTimeout = setTimeout(async () => {
    await fetchPostalCodes(city, exact_match);
  }, delay);
};

// If you need to handle multiple results with their place_ids for getting more details later
export const fetchPostalCodesWithPlaceIdsApi = async (
  setCitiesName,
  setPostalCodes,
  setPostal,
  setLoadingSearch,
  prefix,
  city,
  exact_match = false,
  currentPostalValue = ""
) => {
  try {
    city = city.trim();
    setLoadingSearch(true);

    const queryParams = new URLSearchParams({
      text: city,
      country_code: "fr",
      language: "fr",
      limit: "10",
      include_postal_codes: "true",
    });

    const response = await fetch(`${prefix}places/autocomplete?${queryParams}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.status === "OK" && data.results) {
      // Store the full results with place_ids for later use
      const enrichedResults = data.results.map((place) => ({
        name: place.main_text || place.description,
        fullDescription: place.description,
        placeId: place.place_id,
        postalCode: place.postal_code,
        city: place.city,
        formattedAddress: place.formatted_address,
      }));

      let cities_name = enrichedResults.map((result) => result.name);

      if (exact_match) {
        cities_name = cities_name.filter(
          (name) => name.toLowerCase() === city.toLowerCase()
        );
      }

      // Add the exact user input as the first option
      const userInput = city.charAt(0).toUpperCase() + city.slice(1);
      if (!cities_name.some(c => c.toLowerCase() === userInput.toLowerCase())) {
        cities_name.unshift(userInput);
      }

      // Store both the names and the full results
      setCitiesName(cities_name);
      // You might want to store the full results in another state variable
      // setFullResults(enrichedResults);

      if (cities_name.length > 0) {
        let firstResult = enrichedResults[0];
        let codesPostaux = firstResult.postalCode
          ? [firstResult.postalCode]
          : [];

        // Collect all postal codes from enriched results
        const allPostalCodes = enrichedResults
          .map(r => r.postalCode)
          .filter(Boolean);

        // Remove duplicates
        let uniquePostalCodes = [...new Set(allPostalCodes)];

        // Add current postal value if it exists and not already in the list
        if (currentPostalValue && !uniquePostalCodes.includes(currentPostalValue)) {
          uniquePostalCodes.unshift(currentPostalValue);
        }

        setPostalCodes(uniquePostalCodes.length > 0 ? uniquePostalCodes : codesPostaux);

        // Only set postal if user doesn't have a value yet
        if (!currentPostalValue) {
          setPostal(uniquePostalCodes[0] || codesPostaux[0] || "");
        }
      }
    }

    setLoadingSearch(false);
  } catch (error) {
    console.error("Error fetching postal codes:", error);
    setLoadingSearch(false);
    setCitiesName([]);
    setPostalCodes([]);
    setPostal("");
    return [];
  }
};
